/**
 * Reddit-style default avatars: a member without a photo gets a little
 * character instead of initials. Pure and deterministic, so the same seed
 * (the name every surface already passes to <Avatar>) draws the same
 * character on /profile, /u/:handle, the top bar and every card.
 */

export const AVATAR_BACKGROUNDS = [
  "#7c5cff", // brand violet
  "#ff6b4a", // coral
  "#14b8a6", // teal
  "#f59e0b", // amber
  "#3b82f6", // blue
  "#ec4899", // pink
  "#22c55e", // green
  "#8b5cf6", // purple
  "#0ea5e9", // sky
  "#ef4444", // red
] as const;

export const AVATAR_OUTFITS = ["#1f2937", "#f8fafc", "#facc15", "#fb7185", "#38bdf8", "#a3e635", "#c084fc", "#fb923c"] as const;
export const AVATAR_SKINS = ["#ffffff", "#fde7d4", "#f3c9a5", "#d9a07a", "#a8714f", "#6f4a33"] as const;

export type AvatarEyes = "round" | "happy" | "wink" | "sleepy";
export type AvatarMouth = "smile" | "grin" | "open" | "flat";
export type AvatarHat = "none" | "cap" | "beanie" | "headband" | "antenna";

export interface AvatarTraits {
  background: string;
  outfit: string;
  skin: string;
  eyes: AvatarEyes;
  mouth: AvatarMouth;
  hat: AvatarHat;
  hatColor: string;
  glasses: boolean;
}

const EYES: readonly AvatarEyes[] = ["round", "happy", "wink", "sleepy"];
const MOUTHS: readonly AvatarMouth[] = ["smile", "grin", "open", "flat"];
const HATS: readonly AvatarHat[] = ["none", "cap", "beanie", "headband", "antenna"];

/** FNV-1a: tiny, stable across runtimes, and spreads short names well. */
export function hashSeed(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Each trait draws its own number, so two names that share a background don't also share a face. */
export function avatarTraits(seed: string | null | undefined): AvatarTraits {
  const base = hashSeed((seed ?? "").trim().toLowerCase());
  // mulberry32 over the seed hash: a per-trait remix that uses the high
  // bits, so short palettes (mod 4, mod 8) don't repeat in lockstep.
  let state = base;
  const next = (n: number) => {
    state = (state + 0x6d2b79f5) >>> 0;
    let x = Math.imul(state ^ (state >>> 15), state | 1);
    x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
    return Math.floor((((x ^ (x >>> 14)) >>> 0) / 4294967296) * n);
  };
  // noUncheckedIndexedAccess: next(n) < n, so the index is always in range.
  const pick = <T,>(xs: readonly T[]) => xs[next(xs.length)] as T;
  return {
    background: pick(AVATAR_BACKGROUNDS),
    outfit: pick(AVATAR_OUTFITS),
    skin: pick(AVATAR_SKINS),
    eyes: pick(EYES),
    mouth: pick(MOUTHS),
    hat: pick(HATS),
    hatColor: pick(AVATAR_OUTFITS),
    glasses: next(4) === 0,
  };
}
